import * as EventTracker from '@src/analytics/EventTracker';
import { useContentStudioUI, useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { analytics } from '@src/utility/analytics';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useCallback, useMemo, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { useLocalPreviewVideoUrl } from '../../hooks/useLocalPreviewVideoUrl';
import { saveProducedClips } from '../../services/AIClipProducer';
import CircleCheckIcon from '../../svgs/CircleCheckIcon';
import { PrimaryButton, SecondaryButton } from '../common/dialogStyles';
import FullScreenLoadingOverlay from '../common/FullScreenLoadingOverlay';
import { LoadingState } from '../common/Spinner';
import { MODAL_TOOLTIP_STYLE } from '../common/tooltipStyles';
import AIClip from '../sidepanel/AIClip';
import ClipLimitDropdown from './ClipLimitDropdown';
import ClipSortDropdown from './ClipSortDropdown';
import { SelectionSummaryBar } from './SelectionSummaryBar';
import { clipsInSelectionOrder, getClipKey, useClipSort } from './useClipSort';
import {
  getLocalClipThumbnailFrame,
  useLocalClipProducerThumbnails,
} from './useLocalClipProducerThumbnails';

import type {
  ClipFragment,
  GenerationContext,
  SaveProducedClipsResult,
} from '../../services/AIClipProducer';

const logger = LogManager.getLogger('feature:AIClipProducerResults');

type Props = {
  sourcePlatform?: 'content-studio' | 'creator-studio';
  fragments: ClipFragment[];
  isProducing: boolean;
  onClose: () => void;
  generationContext?: GenerationContext;
};

const AIClipProducerResults = (props: Props) => {
  const {
    fragments,
    isProducing,
    onClose,
    sourcePlatform = 'creator-studio',
    generationContext,
  } = props;
  const rootStore = useStore();
  const videoCreator = useVideoCreatorStore();
  const contentStudioUI = useContentStudioUI();
  const flags = useFlagsCombination(rootStore.userIdentityStore.currentRole);
  // PLAT-11543 (audio-first): saving works before renditions exist. The clip is
  // saved as story-ref + transcript range (metadata only — saveProducedClips
  // forces the lightweight path while renditions are pending), and resolves its
  // real video from the story's renditions when opened. So Save is not gated on
  // rendition readiness.

  const {
    sortedClips,
    sortMode,
    setSortMode,
    clipLimit,
    setClipLimit,
    totalClips,
  } = useClipSort(fragments);

  // Use Set<string> with clip keys for selection that persists across sort order changes
  const [clipsSelected, setClipsSelected] = useState<Set<string>>(new Set());
  const [playingClipIdx, setPlayingClipIdx] = useState<number | null>(null);
  const [isHidden, setIsHidden] = useState(false);
  const renditionPending = isCompressedRenditionPending(videoCreator.story);
  const localPreviewUrl = useLocalPreviewVideoUrl(videoCreator.story?.id);
  const localPosters = useLocalClipProducerThumbnails(
    localPreviewUrl,
    sortedClips,
    renditionPending,
  );

  // The picks, in the order they were picked — a Set is insertion-ordered, so
  // re-sorting the results cannot disturb it (the keys are content-based).
  // Resolved through the clips actually on screen: the grid shows the top N and
  // a re-produce replaces it, so a key whose clip is gone must hold no number
  // and join no reel (PLAT-11093).
  const selectedClips = useMemo(
    () => clipsInSelectionOrder(sortedClips, clipsSelected),
    [sortedClips, clipsSelected],
  );

  // The position each pick takes in a combined reel (PLAT-11093), gapless
  // because it is read off the list above rather than off the selection.
  const selectionPositions = useMemo(
    () =>
      new Map(
        selectedClips.map((clip, index) => [getClipKey(clip), index + 1]),
      ),
    [selectedClips],
  );

  const saveSelectedClips = async (asReel: boolean) => {
    if (selectedClips.length === 0) return;
    setIsHidden(true);
    try {
      if (asReel) {
        EventTracker.trackClipsCombined({
          story_id: videoCreator.story?.id,
          clips_count: selectedClips.length,
          source_platform: sourcePlatform,
        });
      } else {
        EventTracker.trackClipSaved({
          story_id: videoCreator.story?.id,
          clips_count: selectedClips.length,
          source_platform: sourcePlatform,
          as_reel: false,
        });
      }

      // A reel plays in the order the clips were picked, so it reads the
      // selection (PLAT-11093 DS2). Individual clips have no order between
      // them and keep walking the results, as they always have.
      const selectedFragments = asReel
        ? selectedClips
        : sortedClips.filter(fragment =>
            clipsSelected.has(getClipKey(fragment)),
          );

      const result: SaveProducedClipsResult = await saveProducedClips(
        videoCreator,
        selectedFragments,
        sourcePlatform,
        asReel,
        {
          // Disable lightweight save for Creator Studio - clips are always loaded
          // immediately after save, so full render is more appropriate
          enableLightweightClipSave:
            sourcePlatform === 'content-studio' &&
            flags.enableLightweightClipSave,
          useBackgroundQueue: flags.enableBackgroundClipSave,
        },
      );

      // Track recently saved clip IDs for highlight on main page
      const savedIds =
        result.mode === 'queued' ? result.tempIds : result.savedIds;
      if (savedIds.length > 0) {
        contentStudioUI.markClipsSaved(savedIds);
      }

      if (result.mode === 'queued') {
        // Clips are being saved in the background - close immediately
        analytics.track('clips_queued_for_background_save', {
          queuedCount: result.queuedCount,
        });
      }

      setClipsSelected(new Set());
      onClose();
    } catch (e) {
      logger.error('saveSelectedClips: save failed', {
        asReel,
        selectedCount: selectedClips.length,
        ...extractErrorInfo(e),
      });
      // Only the success path closes the modal, so the failure path has to put
      // the results back — otherwise the saving overlay stays up for good.
      setIsHidden(false);
      videoCreator.showToast({
        state: 'error',
        message: 'Saving the clips failed. Please try again.',
      });
    }
  };

  const handleSelectClip = useCallback(
    (clipKey: string) => {
      // The click that lands on an empty selection is pick #1 of a new reel, so
      // it starts the selection over: keys left behind by clips that have left
      // the grid don't come back to lengthen the reel past what the numbers on
      // the cards said (PLAT-11093).
      if (selectedClips.length === 0) {
        setClipsSelected(new Set([clipKey]));
        return;
      }
      setClipsSelected(prevSelected => {
        const newSelected = new Set(prevSelected);
        if (newSelected.has(clipKey)) {
          newSelected.delete(clipKey);
        } else {
          newSelected.add(clipKey);
        }
        return newSelected;
      });
    },
    [selectedClips.length],
  );

  if (isHidden) {
    return <FullScreenLoadingOverlay text="Saving clips…" />;
  }

  return (
    <Wrapper data-testid="ai-clip-producer-results">
      <Title>Select your social clips to save</Title>
      <Heading>
        {sortedClips.length === totalClips
          ? `${totalClips} stories generated`
          : `Showing ${sortedClips.length} of ${totalClips} stories`}
      </Heading>
      <SelectionSummaryBar
        selectedClips={selectedClips}
        generationContext={generationContext}
      />
      <TopBar>
        <DropdownsRow>
          <ClipSortDropdown sortMode={sortMode} onSortChange={setSortMode} />
          <ClipLimitDropdown
            clipLimit={clipLimit}
            onLimitChange={setClipLimit}
          />
        </DropdownsRow>
        <SaveButtonsRow>
          <SaveClipsButton
            disabled={selectedClips.length === 0}
            onClick={() => saveSelectedClips(false)}
            data-testid="save-clips-button"
          >
            <CircleCheckIcon
              strokeColor="currentColor"
              width="16px"
              height="16px"
            />
            <span>Save as Clips</span>
          </SaveClipsButton>
          <SaveReelButton
            disabled={selectedClips.length < 2}
            onClick={() => saveSelectedClips(true)}
            data-tooltip-id="clips-reel-tooltip"
            data-tooltip-content={
              'Save multiple clips back to back in one video'
            }
          >
            <CircleCheckIcon
              strokeColor="currentColor"
              width="16px"
              height="16px"
            />
            <span>Combine Clips</span>
          </SaveReelButton>
          <Tooltip
            id="clips-reel-tooltip"
            place="bottom"
            style={MODAL_TOOLTIP_STYLE}
          />
        </SaveButtonsRow>
      </TopBar>
      <ScrollArea>
        <Clips>
          {sortedClips.map((fragment, index) => {
            const clipKey = getClipKey(fragment);
            const localPoster = localPosters.get(
              getLocalClipThumbnailFrame(fragment),
            );
            const hasLocalProcessingPreview = renditionPending && !!localPoster;
            // Only the clicked card mounts a player on the local upload, so
            // the grid holds at most one media pipeline on that file.
            const playLocally =
              hasLocalProcessingPreview &&
              playingClipIdx === index &&
              !!localPreviewUrl;
            return (
              <AIClip
                key={clipKey}
                index={index}
                fragment={
                  playLocally
                    ? {
                        ...fragment,
                        src: localPreviewUrl,
                        muxPlaybackId: undefined,
                        autoPlay: true,
                      }
                    : fragment
                }
                isSelected={clipsSelected.has(clipKey)}
                selectionOrder={selectionPositions.get(clipKey) ?? null}
                onSelect={() => handleSelectClip(clipKey)}
                isPlaying={playingClipIdx === index}
                onPlay={setPlayingClipIdx}
                aspectRatio={videoCreator.currentVideo?.aspectRatio}
                generationContext={generationContext}
                poster={localPoster}
                suppressDerivedPoster={hasLocalProcessingPreview}
                posterOnly={hasLocalProcessingPreview && !playLocally}
              />
            );
          })}
          {isProducing && (
            <LoadingClipCard>
              <LoadingState message="Generating more clips…" />
            </LoadingClipCard>
          )}
        </Clips>
      </ScrollArea>
    </Wrapper>
  );
};

export default AIClipProducerResults;

const Wrapper = styled.div`
  border-radius: 10px;
  background-color: var(--background);
  width: 942px;
  max-width: calc(100vw - 80px);
  border: 1px solid var(--border);
  height: calc(100vh - 140px);
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 40px 24px 24px;
  /* Was --primary-hover, which made green the inherited default text colour for
     the whole results modal — the dropdown triggers and the clip descriptions
     were rendering green purely by inheritance. */
  color: var(--foreground);
`;

// Takes the height of the clip cards beside it (grid stretch). Alone on a new
// row there is nothing to stretch to, so `min-height` keeps it card-sized —
// without it the card collapsed to its border and the old absolutely-placed
// ring hung below it as a stray circle on a line. One indicator: the ring, so
// the card no longer pulses as well.
const LoadingClipCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  border-radius: 10px;
  border: 1px solid var(--border);
  box-sizing: border-box;
  background-color: var(--surface);
`;

const Title = styled.div`
  width: 100%;
  color: var(--foreground);
  text-align: center;
  font-size: var(--text-xl);
  font-weight: var(--font-weight-bold);
  line-height: normal;
`;

const Heading = styled.div`
  width: 100%;
  text-align: center;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  line-height: 150%;
`;

const TopBar = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DropdownsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SaveButtonsRow = styled.div`
  display: flex;
  gap: 8px;
`;

/**
 * The one primary action in this footer. Was a `styled.div`, so `disabled` only
 * restyled it — but `saveSelectedClips` guards `clipsSelected.size === 0`, so the
 * click-through was harmless here. A real <button> still gains keyboard access
 * and the hover state it never had.
 */
const SaveClipsButton = styled(PrimaryButton)`
  position: relative;
`;

/**
 * Was `styled(SaveClipsButton)` with a transparent background and --primary-hover
 * text — the green-outline pattern the design system names and says to
 * neutralise. Now a plain Secondary: transparent, --foreground, --border.
 *
 * The div→button change matters here. `disabled={clipsSelected.size < 2}` had no
 * counterpart guard in the handler, so clicking this while it looked disabled
 * with a single clip selected called saveSelectedClips(true) and saved a
 * one-clip "reel".
 */
const SaveReelButton = styled(SecondaryButton)`
  position: relative;
`;

const ScrollArea = styled.div`
  position: relative;
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  /* Reserve the bar's width up front, so the 3-column grid doesn't reflow the
     moment the results overflow (or the Clip limit changes). */
  scrollbar-gutter: stable;
  min-height: 0;
  padding: 0 2px;
  mask-image: linear-gradient(
    to bottom,
    transparent 0px,
    black 16px,
    black calc(100% - 16px),
    transparent 100%
  );
`;

const Clips = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 24px;
  padding-bottom: 8px;
`;
